import React, { useEffect, useMemo } from 'react';
import { Form as AntdForm, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import {
  useGetPhdIndividualPlansQuery,
  useGetPhdIndividualPlanSubjectsQuery,
  useGetPhdStudySubjectsQuery,
} from '../../../../../redux/services/phd';

const GRADE_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'Fx'].map((value) => ({
  value,
  label: value,
}));

const PhdStudentEvaluationGradesSection = () => {
  const { t } = useTranslation();
  const form = AntdForm.useFormInstance();
  const studentId = AntdForm.useWatch('phd_student_id', form);
  const schoolYearId = AntdForm.useWatch('phd_school_year_id', form);
  const subjectsGrades = AntdForm.useWatch('subjects_grades', form);

  const { data: plans = [], isFetching: isLoadingPlans } =
    useGetPhdIndividualPlansQuery(undefined, { skip: !studentId });

  const matchingPlan = useMemo(() => {
    if (!studentId) {
      return null;
    }

    return (plans || [])
      .filter((row) => Number(row.phd_student_id) === Number(studentId))
      .sort((a, b) => Number(b.id) - Number(a.id))[0];
  }, [plans, studentId]);

  const { data: planSubjects = [], isFetching: isLoadingSubjects } =
    useGetPhdIndividualPlanSubjectsQuery(
      matchingPlan?.id
        ? { phd_individual_plan_id: matchingPlan.id }
        : undefined,
      { skip: !matchingPlan?.id },
    );
  const { data: allSubjects = [], isFetching: isLoadingAllSubjects } =
    useGetPhdStudySubjectsQuery(undefined, { skip: !matchingPlan?.id });

  const subjectNameMap = useMemo(
    () =>
      new Map(
        (allSubjects || []).map((subject) => [
          Number(subject.id),
          subject.name || `${subject.id}`,
        ]),
      ),
    [allSubjects],
  );

  const subjectRows = useMemo(
    () =>
      (planSubjects || [])
        .filter(
          (row) =>
            Number(row?.phd_individual_plan_id) === Number(matchingPlan?.id),
        )
        .map((row) => ({
          id: row.phd_study_subject_id,
          label:
            row.study_subject?.name ||
            row.studySubject?.name ||
            subjectNameMap.get(Number(row.phd_study_subject_id)) ||
            `${row.phd_study_subject_id}`,
          defaultGrade: row.grade || null,
        })),
    [matchingPlan?.id, planSubjects, subjectNameMap],
  );

  useEffect(() => {
    if (!subjectRows.length) {
      if (form.getFieldValue('subjects_grades') !== null) {
        form.setFieldValue('subjects_grades', null);
      }
      return;
    }

    const existing =
      typeof subjectsGrades === 'object' && subjectsGrades
        ? subjectsGrades
        : {};

    const next = {};
    subjectRows.forEach((subject) => {
      const subjectId = String(subject.id);
      next[subjectId] = Object.prototype.hasOwnProperty.call(
        existing,
        subjectId,
      )
        ? existing[subjectId]
        : subject.defaultGrade || null;
    });

    if (JSON.stringify(existing) !== JSON.stringify(next)) {
      form.setFieldValue('subjects_grades', next);
    }
  }, [form, subjectRows, subjectsGrades]);

  const handleGradeChange = (subjectId, grade) => {
    const current =
      (typeof form.getFieldValue('subjects_grades') === 'object' &&
        form.getFieldValue('subjects_grades')) ||
      {};

    form.setFieldValue('subjects_grades', {
      ...current,
      [String(subjectId)]: grade || null,
    });
  };

  if (!studentId || !schoolYearId) {
    return <div>{t('Najprv vyberte študenta a školský rok.')}</div>;
  }

  if (isLoadingPlans || isLoadingSubjects || isLoadingAllSubjects) {
    return <div>{t('Načítavam predmety individuálneho plánu...')}</div>;
  }

  if (!matchingPlan) {
    return (
      <div>
        {t(
          'Pre vybraného študenta a školský rok neexistuje individuálny plán.',
        )}
      </div>
    );
  }

  if (!subjectRows.length) {
    return <div>{t('Individuálny plán nemá priradené žiadne predmety.')}</div>;
  }

  const current = (typeof subjectsGrades === 'object' && subjectsGrades) || {};

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <span>{t('Predmety a známky')}</span>
        <PhdHelpHint
          label={t('Čo znamenajú typy predmetov S, T a V?')}
          title={t(
            'T je povinný predmet témy, S je študijný predmet a V je voliteľný predmet. Zobrazia sa iba predmety priradené hodnotenému roku.',
          )}
        />
      </div>
      <AntdForm.Item name="subjects_grades" hidden>
        <input type="hidden" />
      </AntdForm.Item>
      <div style={{ display: 'grid', gap: 12 }}>
        {subjectRows.map((subject) => (
          <div
            key={subject.id}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(240px, 1fr) minmax(160px, 220px)',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <div>{subject.label}</div>
            <Select
              allowClear
              value={current[String(subject.id)] || undefined}
              options={GRADE_OPTIONS}
              placeholder={t('Známka')}
              onChange={(value) => handleGradeChange(subject.id, value)}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default PhdStudentEvaluationGradesSection;
